import { useEffect, useRef } from 'react';
import { Box, Grid, CircularProgress, Skeleton, Typography } from '../ui';
import CourseCard from './CourseCard';
import { EmptyState } from '../common';
import { SchoolIcon } from '../icons';
import { Course } from '../../apis/api-client';

interface CourseGridProps {
  courses: Course[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onCourseClick: (course: Course) => void;
  isAuthenticated?: boolean;
}

export default function CourseGrid({ courses, loading, hasMore, onLoadMore, onCourseClick, isAuthenticated = true }: CourseGridProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px', // Start loading 100px before reaching bottom
        threshold: 0.1,
      }
    );

    const currentSentinel = sentinelRef.current;
    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [hasMore, loading, onLoadMore]);

  // Initial loading state
  if (loading && courses.length === 0) {
    return (
      <Grid container spacing={3}>
        {[...Array(8)].map((_, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            <Skeleton variant="text" sx={{ mt: 1 }} />
            <Skeleton variant="text" width="60%" />
          </Grid>
        ))}
      </Grid>
    );
  }

  // Empty state
  if (!loading && courses.length === 0) {
    return (
      <EmptyState
        icon={<SchoolIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
        title="No courses found"
        subtitle="Try adjusting your filters or search criteria"
      />
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {courses.map((course: any) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={course.id}>
            <CourseCard
              course={course}
              onActionClick={() => onCourseClick(course)}
              isAuthenticated={isAuthenticated}
            />
          </Grid>
        ))}
      </Grid>

      {/* Sentinel for infinite scroll */}
      <div ref={sentinelRef} style={{ height: '20px', margin: '20px 0' }} />

      {/* Loading indicator for next page */}
      {loading && courses.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* End of results message */}
      {!hasMore && courses.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No more results
          </Typography>
        </Box>
      )}
    </Box>
  );
}
